import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import iconMap, { getIconComponent } from '../utils/iconMap';

interface IconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
  label?: string;
}

const ALL_ICONS = Object.keys(iconMap).sort();

const IconPicker: React.FC<IconPickerProps> = ({ value, onChange, label = 'Icon' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search.trim()
    ? ALL_ICONS.filter(name =>
        name.toLowerCase().includes(search.trim().toLowerCase())
      )
    : ALL_ICONS;

  const SelectedIcon = value ? getIconComponent(value) : null;

  const handleSelect = (name: string) => {
    onChange(name);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {label && (
        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{label}</label>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-lg bg-white hover:border-[#EF5D22] focus:outline-none focus:ring-2 focus:ring-[#EF5D22]/40 transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          {SelectedIcon ? (
            <>
              <SelectedIcon className="w-5 h-5 text-[#EF5D22] shrink-0" />
              <span className="text-sm font-medium text-gray-800 truncate">{value}</span>
            </>
          ) : (
            <span className="text-sm text-gray-400">Select an icon…</span>
          )}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => e.key === 'Enter' && handleClear(e as any)}
              className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 cursor-pointer"
              title="Clear icon"
            >
              <XMarkIcon className="w-4 h-4" />
            </span>
          )}
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden">
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              autoFocus
              type="text"
              placeholder="Search icons…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
            />
            {search && (
              <button type="button" title="Clear search" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Icon grid */}
          <div className="overflow-y-auto max-h-64 p-2">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-6">No icons match "{search}"</p>
            ) : (
              <div className="grid grid-cols-5 gap-1">
                {filtered.map(name => {
                  const Icon = getIconComponent(name);
                  const isSelected = name === value;
                  return (
                    <button
                      key={name}
                      type="button"
                      title={name}
                      onClick={() => handleSelect(name)}
                      className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg text-xs transition-colors group
                        ${isSelected
                          ? 'bg-[#EF5D22] text-white'
                          : 'hover:bg-orange-50 hover:text-[#EF5D22] text-gray-600'
                        }`}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      <span className="leading-tight text-center break-all line-clamp-2 text-[9px]">
                        {name.replace('Icon', '')}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer count */}
          <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 text-xs text-gray-400 text-right">
            {filtered.length} of {ALL_ICONS.length} icons
          </div>
        </div>
      )}
    </div>
  );
};

export default IconPicker;
